import React from 'react';
import DOMPurify from 'dompurify';
import { fmtDate } from '@/lib/format-date';

// Mirror Zoho's Asset Audit Trail "Details_View": field name in red, value in
// green (#117b0e); for edits, append "Original Value:" + the old value. One
// change per line.
const RED = '#dc2626';
const GREEN = '#117b0e';

// Telemetry meters change every IoT poll — hide them so a mixed entry shows only
// the meaningful edits (matches the server-side trail filter).
const TELEMETRY = new Set([
  'currentOdo', 'currentHubo', 'currentHours', 'currentAddress',
  'online', 'lat', 'lon', 'lastSeenAt', 'iot', 'expiriesSyncedAt',
]);

// Friendly display labels for Asset schema fields. Auto-formatting from
// camelCase (`fmtFieldName` fallback below) mangles compliance acronyms
// (`wofExpiry` → `Wof Expiry`) and reads schema-internal terms verbatim
// (`fleetNumber` instead of the report-column `Fleet #`); this map mirrors
// the Asset report column labels so the audit trail reads the same.
const FIELD_LABELS: Record<string, string> = {
  fleetNumber: 'Fleet #',
  fleetNumberSort: 'Fleet # (sort)',
  registrationNumber: 'Rego #',
  regoExpiry: 'Rego Expiry',
  wofExpiry: 'WoF Expiry',
  cofExpiry: 'CoF Expiry',
  expiriesSyncedAt: 'Expiries Synced At',
  vinNumber: 'VIN',
  chassisNumber: 'Chassis Number',
  engineNumber: 'Engine Number',
  serialNumber: 'Serial Number',
  deviceSerialNumber: 'Device Serial Number',
  deviceType: 'Device Type',
  fuelType: 'Fuel Type',
  fuelMeasurementIn: 'Fuel Measurement In',
  secondaryAuxiliaryMeter: 'Secondary Auxiliary Meter',
  secondaryMeterUnit: 'Secondary Meter Unit',
  trackingProvider: 'Tracking Provider',
  gpsId: 'GPS ID',
  coretexId: 'Coretex ID',
  blackhawkId: 'Blackhawk ID',
  eroadId: 'EROAD ID',
  iot: 'IoT',
  currentOdo: 'Current Odometer',
  currentHubo: 'Current Hubometer',
  currentHours: 'Current Hours',
  currentAddress: 'Current Address',
  trackUsageAs: 'Track Usage As',
  vehicleType: 'Vehicle Type',
  vehicleGroup: 'Vehicle Group',
  vehicleStatus: 'Vehicle Status',
  serviceStatus: 'Service Status',
  servicePlan: 'Service Plan',
  serviceLocation: 'Service Location',
  dispatchGroup: 'Dispatch Group',
  showInDispatch: 'Show in Dispatch',
  assetType: 'Asset Type',
  yearOfManufacture: 'Year of Manufacture',
  nztaRegistered: 'NZTA Registered',
  nztaType: 'NZTA Type',
  subjectToRuc: 'Subject to RUC',
  grossMassWeight: 'Gross Mass Weight',
  grossUnladenWeight: 'Gross Unladen Weight',
  lastSeenAt: 'Last Seen At',
  offboardReason: 'Offboard Reason',
  offboardAdditionalInfo: 'Offboard Additional Info',
  offboardedAt: 'Offboarded At',
  offboardedBy: 'Offboarded By',
  photoKey: 'Photo Key',
  zohoId: 'Zoho ID',
  vehicleZohoId: 'Vehicle Zoho ID',
  dispatchVehicleZohoId: 'Dispatch Vehicle Zoho ID',
};

function fmtFieldName(field: string): string {
  const m = FIELD_LABELS[field];
  if (m) return m;
  // Fallback for any field not in the map: camelCase → Title Case.
  const spaced = field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Ref fields are audited as the populated object on one side and a raw id on
 *  the other; reduce either to a comparable id so we can drop no-op "changes"
 *  (same id, logged only because object≠string). The CRUD factory's
 *  `renameIdRecursively` rewrites `_id` → `id` everywhere on the wire,
 *  including inside `changes[].oldValue`/`newValue` populated subdocs — so
 *  we accept either spelling here. */
function idOf(v: any): string | null {
  if (v == null) return null;
  if (typeof v === 'object') {
    const id = (v as any)._id ?? (v as any).id;
    return id != null ? String(id) : null;
  }
  return String(v);
}
function sameValue(a: unknown, b: unknown): boolean {
  const ia = idOf(a), ib = idOf(b);
  return ia != null && ia === ib;
}

function fmtVal(v: unknown): string {
  if (v == null || v === '') return '(empty)';
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const idFallback = (o._id ?? o.id) as unknown;
    return (o.name || o.group || o.code || o.type || o.description || o.fullName || (idFallback ? String(idFallback) : JSON.stringify(o))) as string;
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return fmtDate(d);
    }
    return v;
  }
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

/** Historical (Zoho-imported) rows carry pre-rendered red/green Details_View
 *  HTML. Sanitize with DOMPurify (allowlist) before rendering — the previous
 *  regex-based blocklist was bypassable (e.g. `<img/onerror=…>`, `javascript:`
 *  hrefs). We keep only inline text-formatting tags + the `style`/`color`
 *  attributes that carry the red/green diff colours; everything else — scripts,
 *  event handlers, `javascript:`/`data:` URIs, iframes, links — is stripped.
 *  (Pentest A-03: stored XSS via dangerouslySetInnerHTML.) */
const AUDIT_HTML_CONFIG = {
  ALLOWED_TAGS: ['div', 'span', 'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'font'],
  ALLOWED_ATTR: ['style', 'color'],
  ALLOW_DATA_ATTR: false,
};
function legacyHtml(html: string) {
  const safe = DOMPurify.sanitize(html, AUDIT_HTML_CONFIG);
  return <div className="text-xs leading-relaxed whitespace-pre-line" dangerouslySetInnerHTML={{ __html: safe }} />;
}

export function AuditChangeCell({ row }: { row: any }) {
  // Merged trail: legacy (Zoho) rows store pre-rendered HTML in `details`;
  // native rows carry structured `changes`. Render whichever is present.
  if (typeof row?.details === 'string' && row.details.trim()) return legacyHtml(row.details);
  const all: Array<{ field: string; oldValue: unknown; newValue: unknown }> = row?.changes ?? [];
  // Hide telemetry meters and no-op (same-id) diffs from historical bulk re-saves.
  const changes = all.filter((c) => !TELEMETRY.has(c.field) && !sameValue(c.oldValue, c.newValue));
  const isUpdate = row?.operation === 'update';
  if (!changes.length) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-0.5 text-xs leading-relaxed">
      {changes.map((c, i) => (
        <div key={i}>
          <span style={{ color: RED }}>{fmtFieldName(c.field)}</span>
          <span>: </span>
          <span style={{ color: GREEN }}>{fmtVal(c.newValue)}</span>
          {isUpdate && (
            <>
              <span style={{ color: RED }}> Original Value: </span>
              <span style={{ color: GREEN }}>{fmtVal(c.oldValue)}</span>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
